import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { copyTextToClipboard } from '../utils/clipboard';
import { SecurityAuthModal } from './SecurityAuthModal';

const MASK = '••••••••••••••••';

export const SecureText = ({ label, value, clipboardClearTimeoutMs, autoAction, onCopied }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isReauthOpen, setIsReauthOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'reveal' | 'copy'

  const {
    isSensitiveAuthFresh,
    clipboardClearTimeoutMs: clipboardTimeoutFromContext,
  } = useAuth();

  const effectiveClipboardTimeoutMs = clipboardClearTimeoutMs ?? clipboardTimeoutFromContext ?? 30000;

  const revealValue = () => {
    setIsVisible(true);
    window.setTimeout(() => setIsVisible(false), 15000);
  };

  const copyValue = async () => {
    if (!value) return;
    await copyTextToClipboard(value, effectiveClipboardTimeoutMs);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
    onCopied?.();
  };

  const executePendingAction = async (action) => {
    if (action === 'reveal') {
      revealValue();
      return;
    }
    await copyValue();
  };

  const requestSensitiveAction = async (action) => {
    if (isSensitiveAuthFresh && isSensitiveAuthFresh()) {
      await executePendingAction(action);
      return;
    }
    setPendingAction(action);
    setIsReauthOpen(true);
  };

  useEffect(() => {
    if (autoAction === 'copy') {
      void requestSensitiveAction('copy');
    }
  }, [autoAction]);

  const handleReveal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isVisible) {
      setIsVisible(false);
      return;
    }
    requestSensitiveAction('reveal');
  };

  const handleCopy = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    requestSensitiveAction('copy');
  };

  return (
    <>
      <div style={{ marginBottom: '16px', padding: '16px', background: 'var(--bg-surface)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
        <div className="input-label" style={{ textTransform: 'uppercase', fontSize: '12px' }}>{label}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '12px' }}>
          <div style={{ fontSize: '18px', fontFamily: 'monospace', flex: 1, color: 'var(--text-primary)', overflowWrap: 'anywhere', letterSpacing: isVisible ? 'normal' : '2px', userSelect: isVisible ? 'all' : 'none' }}>
            {isVisible ? value : MASK}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={handleReveal}
              style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
              aria-label={isVisible ? 'Hide password' : 'Show password'}
              title={isVisible ? 'Hide password' : 'Show password'}
            >
              {isVisible ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={handleCopy}
              style={{ background: 'transparent', border: 'none', color: copied ? 'var(--success, #10b981)' : 'var(--accent-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
              aria-label="Copy password"
              title="Copy password"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
          </div>
        </div>
      </div>

      <SecurityAuthModal
        open={isReauthOpen}
        action={pendingAction}
        onClose={() => {
          setIsReauthOpen(false);
          setPendingAction(null);
        }}
        onSuccess={async () => {
          setIsReauthOpen(false);
          await executePendingAction(pendingAction);
          setPendingAction(null);
        }}
      />
    </>
  );
};
